<template>
  <ContentSection :spacing="conditionalSpacing">
    <Container
      class="px-container lg:px-container-wide xl:px-container-desktop-wide"
    >
      <div :class="{ group: !imageOrVideo.video }">
        <div
          class="grid grid-cols-1 items-center gap-7 sm:gap-11 lg:grid-cols-2 lg:gap-0"
          :class="
            data.aspect_ratio.value === '66'
              ? 'lg:grid-cols-3'
              : 'lg:grid-cols-2'
          "
        >
          <!-- TEXT -->
          <div
            class="px-4 transition-transform duration-300 hover:-translate-y-3 sm:group-hover:-translate-y-3 lg:px-0"
            :class="[
              data.layout.value === 'right' ? 'order-1' : 'order-2',
              {
                'lg:px-18':
                  data.aspect_ratio.value === '50' &&
                  data.layout.value === 'left',
                'lg:pr-18':
                  data.aspect_ratio.value === '50' &&
                  data.layout.value === 'right',
                'lg:px-11':
                  data.aspect_ratio.value === '66' &&
                  data.layout.value === 'left',
                'lg:pr-11':
                  data.aspect_ratio.value === '66' &&
                  data.layout.value === 'right',
                group: imageOrVideo.video,
              },
            ]"
          >
            <h2
              v-if="data.heading"
              class="mb-5 max-w-title text-3xl font-medium sm:mb-8 sm:text-5xl"
              v-html="$replaceHtmlTags(data.heading)"
            />

            <Bard
              v-if="data.text"
              class="max-w-text text-base sm:text-md lg:mb-11"
              :data="data.text"
            />
            <!-- BUTTON DESKTOP -->
            <AppButton
              class="hidden lg:flex"
              :to="data.link"
              :target="$useTarget(data.link, data.target?.value)"
            >
              {{ data.link_text }}
            </AppButton>
          </div>

          <!-- IMAGE OR VIDEO -->
          <Component
            :is="divOrNuxtLink"
            class="size-full"
            :class="[
              data.layout.value === 'right' ? 'order-2' : 'order-1',
              data.aspect_ratio.value === '66' ? 'lg:col-span-2' : '',
            ]"
            :aria-label="data.link_text"
            v-bind="data.link && !imageOrVideo.video ? { to: data.link } : {}"
            :target="$useTarget(data.link, data.target?.value)"
          >
            <div class="relative aspect-video overflow-hidden rounded-sm">
              <AppImage
                v-if="imageOrVideo.image"
                :src="imageOrVideo.image"
                :alt="
                  $i18n.locale === 'fr'
                    ? imageOrVideo.image.alt_fr
                    : imageOrVideo.image.alt
                "
                loading="lazy"
                :style="`object-position: ${imageOrVideo.image.focus_css};`"
                class="size-full object-cover transition-transform duration-300 ease-in-out group-hover:scale-105"
              />

              <VideoPlayer
                v-else-if="imageOrVideo.video"
                :src="imageOrVideo.video"
                class="aspect-video w-full transition-transform duration-300 ease-in-out"
                :class="{ 'sm:group-hover:scale-105': !imageOrVideo.video }"
                :cover-image="imageOrVideo.cover_image"
                :youtube="{ controls: 1 }"
                keep-aspect-video
              />

              <!-- VIDEO HOVER OVERLAY GRADIENT -->
              <div
                v-show="imageOrVideo.image && !imageOrVideo.video"
                class="pointer-events-none absolute inset-x-0 top-0 z-10 h-1/4 bg-gradient-to-b from-[#063750]/40 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100"
              />
              <div
                v-show="imageOrVideo.image && !imageOrVideo.video"
                class="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-1/4 bg-gradient-to-t from-[#063750]/40 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100"
              />
            </div>
          </Component>
        </div>

        <!-- BUTTON MOBILE -->
        <AppButton
          class="mt-8 lg:hidden"
          :to="data.link"
          :class="{ 'mx-4': data.layout.value === 'left' }"
        >
          {{ data.link_text }}
        </AppButton>
      </div>
    </Container>
  </ContentSection>
</template>

<script setup>
const props = defineProps({
  data: { type: Object, default: null },
  prevComponent: { type: String, default: null },
  nextComponent: { type: String, default: null },
})

const imageOrVideo = computed(() => {
  return props.data?.image_or_video?.[0] || false
})

// determine whether to load NuxtLink or button
const divOrNuxtLink = computed(() => {
  if (props.data.link && !imageOrVideo.value.video) {
    return resolveComponent('NuxtLink')
  }

  return 'div'
})

const divOrNuxtLinkText = computed(() => {
  if (props.data.link && imageOrVideo.value.video) {
    return resolveComponent('NuxtLink')
  }

  return 'div'
})

const conditionalSpacing = useConditionalSpacing(
  props.prevComponent,
  props.nextComponent,
  ['teaser_text_and_image_or_video']
)
</script>
